import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes, NotificationTypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import { getIntl } from '../../utils/intl';
import urls from '../../utils/urls';
import { getProfileInAppNotificationKey, onToggleInAppNotificationReadStatus } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useMemo } from 'react';
import useNotify from '../useNotify';

const intl = getIntl('UseProfileInAppNotifications', {
    loadError: 'Error loading messages',
    updateReadStatusError: 'Error updating messages',
    updateReadStatusSuccess: 'Messages updated',
});

export type UseProfileInAppNotificationsArgs = {
    medicalProfileId: string;
    onReadStatusToggle?: () => void;
};

export type ToggleReadStatusArgs = {
    preventSuccessNotification?: boolean;
} & APITypes.medicalProfileService.ToggleInAppNotificationsReadStatusRequest;

export type UseProfileInAppNotificationsResponse = {
    allNotifications: NotificationTypes.ReadOrUnreadInAppNotification[];
    unReadNotifications: NotificationTypes.UnreadInAppNotification[];
    readNotifications: NotificationTypes.ReadInAppNotification[];
    isLoadingNotifications: boolean;
    toggleReadStatus: (request: ToggleReadStatusArgs) => void;
    isTogglingReadStatus: boolean;
};

const emptyArray: NotificationTypes.ReadOrUnreadInAppNotification[] = [];

export const useProfileInAppNotifications = ({
    medicalProfileId,
    onReadStatusToggle,
}: UseProfileInAppNotificationsArgs): UseProfileInAppNotificationsResponse => {
    const notify = useNotify();
    const validateRegistrationCodeQuery = useQuery({
        queryKey: getProfileInAppNotificationKey(medicalProfileId),
        queryFn: async () => {
            const response = await appAPI.get<APITypes.medicalProfileService.GetAllProfileInAppNotificationsResponse>(
                urls.api.medicalProfiles.inAppNotifications.allNotifications(medicalProfileId),
            );
            return response;
        },
    });
    useQueryErrorNotify(validateRegistrationCodeQuery, intl('loadError'));

    const toggleReadStatusMutation = useMutation({
        mutationFn: async (request: ToggleReadStatusArgs) => {
            const { preventSuccessNotification: _, ...requestBody } = request;
            await appAPI.post(
                urls.api.medicalProfiles.inAppNotifications.toggleInAppNotificationReadStatus(medicalProfileId),
                {
                    body: requestBody,
                },
            );
        },
        onSuccess: (_data, args) => {
            onToggleInAppNotificationReadStatus.resetCache({ medicalProfileId })();
            if (!args.preventSuccessNotification) {
                notify('success', intl('updateReadStatusSuccess'));
            }
            onReadStatusToggle?.();
        },
        onError: () => {
            notify('error', intl('updateReadStatusError'));
        },
    });

    const allNotifications = useMemo(
        () =>
            [...(validateRegistrationCodeQuery.data?.profileInAppNotifications || emptyArray)].sort(
                (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
            ),
        [validateRegistrationCodeQuery.data],
    );

    const unReadNotifications = useMemo(
        () =>
            allNotifications
                .filter((notification) => !notification.read)
                .map((_) => _ as NotificationTypes.UnreadInAppNotification),
        [allNotifications],
    );
    const readNotifications = useMemo(
        () =>
            allNotifications
                .filter((notification) => notification.read)
                .map((_) => _ as NotificationTypes.ReadInAppNotification),
        [allNotifications],
    );

    return {
        allNotifications,
        unReadNotifications,
        readNotifications,
        isLoadingNotifications: validateRegistrationCodeQuery.isLoading,
        toggleReadStatus: toggleReadStatusMutation.mutate,
        isTogglingReadStatus: toggleReadStatusMutation.isPending,
    };
};
